import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { Assessment } from 'modules/dashboard/core/type';

import { AnalyticData, Item } from '../type';

import { useString } from 'hooks';
import { apiCall, useFetchApiToState } from 'hooks/service';
import { isEmpty } from 'lodash';

import { AnalyticsEnum } from '../enum';
import { linearRegression } from './helper';

export const useAnalyticsController = () => {
  const params = useParams();

  const { id } = params;

  const userProfile = useAuthStore(userProfileSelector);

  const institutionId = userProfile?.institution?.id ?? id;

  const [assessment, setAssessment] = useState<{ value: string; label: string }>();

  const [analyticsData, setAnalyticsData] = useState<AnalyticData>();

  const searchSelect = useString('');

  const [hideData, setHideData] = useState<{ key: string; hide: boolean }[]>([
    { key: AnalyticsEnum.records, hide: false },
    { key: AnalyticsEnum.normal, hide: false },
    { key: AnalyticsEnum.afterExercise, hide: false },
  ]);

  const assessmentsState = useFetchApiToState<any, any>({
    url: '/assessments/on-boarding-default',
    data: {
      institutionId,
    },
    method: 'GET',
  });

  const assessments =
    assessmentsState.state?.functionalTests?.map((elAssessment: Assessment) => ({
      value: elAssessment.id,
      label: elAssessment.name,
    })) || [];

  useEffect(() => {
    assessmentsState.fetchApi();
  }, []);

  useEffect(() => {
    if (!isEmpty(assessments)) {
      setAssessment(assessments[0]);
    }
  }, [JSON.stringify(assessments)]);

  useEffect(() => {
    if (assessment?.value && institutionId) {
      getAnalyticsData();
    }
  }, [assessment?.value, institutionId]);

  const getAnalyticsData = async () => {
    const result: AnalyticData = await apiCall({
      url: '/data-analytics/on-boarding',
      method: 'GET',
      isLoading: true,
      data: {
        institutionId,
        functionalTest: assessment?.value,
      },
    });

    if (!isEmpty(result)) {
      setAnalyticsData(result);
    }
  };

  const onChangeAssessment = (item: any) => {
    if (!item) {
      return;
    }

    setAssessment(item);
  };

  const onChangeSearchAssessment = (item: string) => {
    searchSelect.setValue(item);
  };

  const onClickHideData = (key: string) => {
    const index = hideData?.findIndex((el) => el.key === key);

    const newHideData = [...hideData];

    if (index !== -1) {
      newHideData[index] = { key, hide: !newHideData[index].hide };
    } else {
      newHideData.push({ key, hide: true });
    }

    setHideData([...newHideData]);
  };

  const getTrendLineData = (data: Item[]) => {
    const xValues = data.map((record) => record.x);
    const yValues = data.map((record) => record.y);

    // Calculate linear regression
    const regression = linearRegression(xValues, yValues);

    // Get the slope and intercept
    const slope = regression.slope as number;
    const intercept = regression.intercept as number;

    return [
      { x: 60, y: slope * 60 + intercept },
      { x: 90, y: slope * 90 + intercept },
    ];
  };

  return {
    assessments,
    assessment,
    searchSelect,
    onChangeAssessment,
    onChangeSearchAssessment,
    analyticsData,
    onClickHideData,
    hideData,
    getTrendLineData,
  };
};
